// Subtitle audio recorder of the page editor: shows the page text to read, records with the
// microphone, plays the take back and uploads it as the page's audio.
import React, { useEffect, useRef, useState } from "react"
import Modal from "aldoo-ra/Modal"
import Button from "aldoo-ra/Button"
import AudioPlayer from "aldoo-ra/AudioPlayer"
import { MicrophoneIcon, StopIcon } from "@heroicons/react/24/outline"
import useProductEditorTexts from "aldoo-ra/CMSModels/StoreProduct/useProductEditorTexts"

export default function AudioRecorder({ open, title, textContent, busy, onUseRecording, onNextPage, onPreviousPage, onClose }) {
  const t = useProductEditorTexts()
  const [recording, setRecording] = useState(false)
  const [take, setTake] = useState(null) // { blob, url, seconds }
  const [error, setError] = useState("")
  const recorder = useRef(null)
  const chunks = useRef([])
  const startedAt = useRef(0)

  useEffect(() => {
    if (open) return
    stop()
    setTake(null)
  }, [open])

  const start = async () => {
    setError("")
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false })
      const rec = new MediaRecorder(stream)
      chunks.current = []
      rec.ondataavailable = (e) => e.data.size && chunks.current.push(e.data)
      rec.onstop = () => {
        stream.getTracks().forEach((t) => t.stop())
        const blob = new Blob(chunks.current, { type: rec.mimeType || "audio/webm" })
        setTake({ blob, url: URL.createObjectURL(blob), seconds: (Date.now() - startedAt.current) / 1000 })
      }
      recorder.current = rec
      startedAt.current = Date.now()
      rec.start()
      setRecording(true)
    } catch (e) {
      setError(t`Microphone access is not available`)
    }
  }

  const stop = () => {
    if (recorder.current?.state === "recording") recorder.current.stop()
    recorder.current = null
    setRecording(false)
  }

  const use = () => {
    if (!take) return
    const ext = take.blob.type.includes("ogg") ? "ogg" : take.blob.type.includes("mp4") ? "m4a" : "webm"
    onUseRecording(new File([take.blob], `recording-${Date.now()}.${ext}`, { type: take.blob.type }))
  }

  if (!open) return null
  return (
    <Modal isOpen onClose={onClose} variant="admin_compact" title={title} styleOverrides={{ container: "bg-admin_paper w-full max-w-[720px] rounded-md border border-admin_bordered shadow-lg" }}>
      <div className="flex flex-col gap-4 p-4" data-testid="audio-recorder">
        <div className="p-4 rounded-md border-[3px] border-dashed border-gray-400 min-h-[120px] text-lg leading-relaxed whitespace-pre-wrap">{textContent || <span className="text-admin_text_secondary">{t`No text on this page`}</span>}</div>
        <div className="flex items-center gap-3">
          {!recording ? (
            <Button variant="admin_primary" onClick={start} disabled={busy} className="px-4"><MicrophoneIcon className="w-5 h-5" /> {t`Record`}</Button>
          ) : (
            <Button variant="admin_primary_red" onClick={stop} className="px-4"><StopIcon className="w-5 h-5" /> {t`Stop`}</Button>
          )}
          {recording && <span className="text-sm text-admin_error animate-pulse">{t`Recording…`}</span>}
          <span className="flex-1" />
          <Button variant="admin_secondary" className="h-[32px] text-xs px-3" onClick={onPreviousPage} disabled={busy || recording}>{t`Previous page`}</Button>
          <Button variant="admin_secondary" className="h-[32px] text-xs px-3" onClick={onNextPage} disabled={busy || recording}>{t`Next page`}</Button>
        </div>
        {error && <p className="text-sm text-admin_error my-0">{error}</p>}
        {take && <AudioPlayer src={take.url} title={t`Take`} initialDuration={take.seconds} showRate={false} />}
        <div className="flex justify-end gap-2">
          <Button variant="admin_secondary" onClick={onClose} disabled={busy}>{t`Close`}</Button>
          <Button variant="admin_primary" onClick={use} disabled={!take || busy || recording}>{busy ? t`Uploading…` : t`Use recording`}</Button>
        </div>
      </div>
    </Modal>
  )
}
